// src/components/Projeto/ProjetoList.jsx

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Pagination,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  Chat as ChatIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ChatService from '../../services/ChatService';
import ChatDialog from '../ChatDialog';
import { listarProjetos } from '../../services/projetoService';

function ProjetoList({ data: dataProp, onUpdate, userRole, currentUser }) {
  const navigate = useNavigate();
  const [fetchedData, setFetchedData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    if (!dataProp) {
      setLoading(true);
      listarProjetos()
        .then(res => setFetchedData(Array.isArray(res.data) ? res.data : []))
        .catch(() => setFetchError('Erro ao carregar projectos.'))
        .finally(() => setLoading(false));
    }
  }, [dataProp]);

  const data = dataProp ?? fetchedData;

  // Estados do chat — todos os hooks antes de qualquer return
  const [page, setPage] = useState(1);
  const [chatDialogOpen, setChatDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [chatMessage, setChatMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [loadingChat, setLoadingChat] = useState(false);

  if (loading) return <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>;
  if (fetchError) return <Alert severity="error">{fetchError}</Alert>;

  // Paginação
  const pageSize = 5;
  const totalPages = Math.ceil(data.length / pageSize);
  const startIndex = (page - 1) * pageSize;
  const projetosPagina = data.slice(startIndex, startIndex + pageSize);

  // Abre o chat e carrega o histórico via API
  const openChatDialog = async (project) => {
    console.log("Abrindo chat para o projeto:", project);
    setSelectedProject(project);
    setChatDialogOpen(true);
    setLoadingChat(true);
    try {
      const response = await ChatService.getMessages('projeto', project.id);
      setChatHistory(response.data);
    } catch (error) {
      console.error("Erro ao carregar histórico de chat:", error);
    } finally {
      setLoadingChat(false);
    }
  };

  // Fecha o diálogo de chat e limpa os estados relacionados
  const closeChatDialog = () => {
    setChatDialogOpen(false);
    setSelectedProject(null);
    setChatMessage('');
    setChatHistory([]);
  };

  // Envia a mensagem e recarrega o histórico
  const sendChatMessage = async () => {
    if (chatMessage.trim() === '') return;
    const payload = {
      context: 'projeto',
      context_id: selectedProject ? selectedProject.id : null,
      sender: currentUser,
      message: chatMessage
    };
    console.log("Payload enviado:", payload);
    try {
      await ChatService.sendMessage(payload);
      setChatMessage('');
      const response = await ChatService.getMessages('projeto', selectedProject.id);
      setChatHistory(response.data);
    } catch (error) {
      console.error("Erro ao enviar mensagem:", error);
    }
  };

  return (
    <>
      {projetosPagina.map((projeto) => (
        <Paper
          key={projeto.id}
          elevation={0}
          sx={{
            p: 3,
            mb: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" gutterBottom>
                {projeto.proposta?.titulo || 'Sem Título'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Estudante:</strong> {projeto.proposta?.estudante?.nome || 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Submetido em:</strong> {new Date(projeto.created_at).toLocaleDateString()}
              </Typography>
              <Chip
                label={projeto.status}
                color={projeto.status === 'APROVADO' ? 'success' : 'warning'}
                size="small"
                sx={{ mt: 1 }}
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: '150px' }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<VisibilityIcon />}
                onClick={() => navigate(`/projetos/${projeto.id}`)}
              >
                Visualizar
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={() => {
                  if (projeto.arquivoUrl) {
                    window.open(projeto.arquivoUrl, '_blank');
                  } else {
                    alert('Arquivo indisponível.');
                  }
                }}
              >
                Baixar
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ChatIcon />}
                onClick={() => openChatDialog(projeto)}
              >
                Chat
              </Button>
            </Box>
          </Box>
        </Paper>
      ))}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            color="primary"
            count={totalPages}
            page={page}
            onChange={(_, newPage) => setPage(newPage)}
          />
        </Box>
      )}
      {/* Diálogo de Chat utilizando o componente ChatDialog */}
      <ChatDialog
        open={chatDialogOpen}
        onClose={closeChatDialog}
        context="projeto"
        contextId={selectedProject?.id || 0}
        currentUser={currentUser}
        title={`Chat com ${selectedProject?.proposta?.estudante?.nome || 'Estudante'}`}
        messages={chatHistory}
        loading={loadingChat}
        onSendMessage={sendChatMessage}
        message={chatMessage}
        onMessageChange={(e) => setChatMessage(e.target.value)}
        onRefresh={async () => {
          setLoadingChat(true);
          try {
            const response = await ChatService.getMessages('projeto', selectedProject.id);
            setChatHistory(response.data);
          } catch (error) {
            console.error("Erro ao atualizar histórico de chat:", error);
          } finally {
            setLoadingChat(false);
          }
        }}
      />
    </>
  );
}

ProjetoList.propTypes = {
  data: PropTypes.array.isRequired,
  onUpdate: PropTypes.func,
  userRole: PropTypes.string,
  currentUser: PropTypes.string.isRequired
};

export default ProjetoList;
